/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { useDispatch, useSelector } from 'react-redux';
import { useState, useEffect } from 'react';
import { RoutePath } from 'shared/enums/RoutePath';
import { ThunkStatus } from '../../../enums/ThunkStatus';
import { ConfigResourceProps } from '../../../enums/ConfigResourceProps';
import { useConfiguration } from '../../configuration/useConfiguration';
import {
  resetOrganization,
  selectOrganization,
  selectResetOrg,
  selectStatus,
  updateOrganization,
} from './organization.editor.slice';
import { editOrganization, getOrganizationById, saveOrganization } from './organization.editor.thunk';
import { handleRedirectAndReload } from '../../../../shared/utils/routingUtils';

export default function useOrganizationEditor(organizationId) {
  const [isValid, setIsValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { resource: validationSchema } = useConfiguration(ConfigResourceProps.ORGANIZATION_VALIDATION_SCHEMA);
  const organizationUiSchema = useConfiguration(ConfigResourceProps.ORGANIZATION_UI_SCHEMA);

  const dispatch = useDispatch();

  const organization = useSelector(selectOrganization);
  const status = useSelector(selectStatus);
  const shouldResetOrg = useSelector(selectResetOrg);
  const onUpdateOrganization = newOrganization => dispatch(updateOrganization(newOrganization));

  const checkIfFormIsValid = (form) => {
    const { required } = validationSchema;
    const { formData } = form;
    // THIS IS AN ATROCITY, TO REFACTOR WHEN BUSINESS RULES ARE IMPLEMENTED IN THE BE
    let hasEmptyProperty = false;
    for (const requiredField of required) {
      if (!formData?.[requiredField]) {
        hasEmptyProperty = true;
      }
    }
    setIsValid(form.errors?.length === 0 && !hasEmptyProperty && !form.isInvalid);
  };

  useEffect(() => {
    return () => {
      dispatch(resetOrganization());
    };
  }, []);

  useEffect(() => {
    if (organizationId) dispatch(getOrganizationById(organizationId));
  }, [organizationId]);

  useEffect(() => {
    setIsLoading(status === ThunkStatus.LOADING);
  }, [status]);

  useEffect(() => {
    if (shouldResetOrg) {
      if (organizationId) {
        handleRedirectAndReload(RoutePath.generateRoutePath(RoutePath.USER_SEARCH, { organization: organizationId }));
      } else {
        handleRedirectAndReload(RoutePath.SWITCH_ACCOUNT);
        dispatch(resetOrganization());
      }
    }
  }, [shouldResetOrg]);

  const onSubmit = () => {
    if (!organizationId) {
      dispatch(saveOrganization(organization));
      return;
    }

    dispatch(editOrganization({ orgId: organizationId, organization }));
  };

  return {
    uiSchema: organizationUiSchema.resource,
    validationSchema,
    organization,
    isValid,
    isLoading,
    checkIfFormIsValid,
    onSubmit,
    onUpdateOrganization,
  };
}
